// @flow
import { TimePicker } from 'antd'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import * as React from 'react'

import BaseField, { type Props as FieldProps } from './BaseField'

dayjs.extend(customParseFormat)
dayjs.extend(utc)

type Props = {
  ...FieldProps,
  id?: string,
  name?: string,
  onChange: (value: null | [[Date, Date], [string, string]]) => void,
  format?: string,
  size?: 'small' | 'middle' | 'large',
  defaultValue?: [null | string, null | string],
  value?: [null | string, null | string],
  disabled?: boolean,
  utcValue?: boolean,
  valueParserFormat?: 'YYYY-MM-DDTHH:mm:ss',
  showPastOnly?: boolean,
  showFutureOnly?: boolean,
}

function TimeRangeField({
  onChange,
  id,
  name,
  format = 'HH:mm',
  size = 'large',
  defaultValue,
  value,
  disabled,
  utcValue,
  valueParserFormat = 'YYYY-MM-DDTHH:mm:ss',
  showPastOnly,
  showFutureOnly,
  ...rest
}: Props): React.Node {
  const valueIsString = React.useMemo(() => {
    if (!value) {
      return false
    }
    return typeof value[0] === 'string' || typeof value[1] === 'string'
  }, [value])

  const disabledTime = React.useCallback(
    (now, _) => {
      if (showPastOnly || showFutureOnly) {
        const minTime = showFutureOnly ? now.toDate() : undefined
        const maxTime = showPastOnly ? now.toDate() : undefined
        const disabledHours = []
        const disabledMinutes = []

        if (minTime) {
          for (let i = 0; i < minTime.getHours(); i++) {
            disabledHours.push(i)
          }
          for (let i = 0; i < minTime.getMinutes(); i++) {
            disabledMinutes.push(i)
          }
        }

        if (maxTime) {
          for (let i = 23; i > maxTime.getHours(); i--) {
            disabledHours.push(i)
          }
          for (let i = 59; i > maxTime.getMinutes(); i--) {
            disabledMinutes.push(i)
          }
        }

        return {
          disabledHours: () => disabledHours,
          disabledMinutes: () => disabledMinutes,
        }
      }
      return undefined
    },
    [showFutureOnly, showPastOnly]
  )

  return (
    <BaseField {...rest}>
      <TimePicker.RangePicker
        defaultValue={
          defaultValue
            ? [
                defaultValue
                  ? dayjs(
                      defaultValue[0],
                      valueIsString ? valueParserFormat : undefined
                    )
                  : undefined,
                defaultValue
                  ? dayjs(
                      defaultValue[1],
                      valueIsString ? valueParserFormat : undefined
                    )
                  : undefined,
              ]
            : undefined
        }
        disabled={disabled}
        disabledTime={disabledTime}
        format={
          format
            ? {
                format,
                type: 'mask',
              }
            : undefined
        }
        id={id || name}
        name={name ?? 'time_range_field'}
        onChange={(times, timesString) => {
          onChange(
            times
              ? [
                  times.map((time) =>
                    utcValue ? time.utc().toDate() : time.toDate()
                  ),
                  timesString,
                ]
              : null
          )
        }}
        style={{ paddingTop: '0.438rem', paddingBottom: '0.438rem' }}
        value={
          value
            ? [
                value[0]
                  ? dayjs(
                      value[0],
                      valueIsString ? valueParserFormat : undefined
                    )
                  : undefined,
                value[1]
                  ? dayjs(
                      value[1],
                      valueIsString ? valueParserFormat : undefined
                    )
                  : undefined,
              ]
            : undefined
        }
      />
    </BaseField>
  )
}

export default TimeRangeField
